import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { games, type GameInfo } from "./lib/catalogue";
import { hasPractice, practicePath } from "./lib/practice";

export function Icon({ name, size = 20 }: { name: string; size?: number }) {
  const paths: Record<string, ReactNode> = {
    sun: (
      <>
        <circle cx="12" cy="12" r="4" />
        <path d="M12 2v2m0 16v2M2 12h2m16 0h2M5 5l1.5 1.5m11 11L19 19M5 19l1.5-1.5m11-11L19 5" />
      </>
    ),
    moon: <path d="M20.5 14A9 9 0 0 1 10 3.5 9 9 0 1 0 20.5 14Z" />,
    system: (
      <>
        <circle cx="12" cy="12" r="8.5" />
        <path d="M12 3.5v17a8.5 8.5 0 0 0 0-17Z" fill="currentColor" />
      </>
    ),
    arrow: <path d="M5 12h14m-6-6 6 6-6 6" />,
    close: <path d="m6 6 12 12M6 18 18 6" />,
    check: <path d="m5 12 4 4L19 6" />,
    chevron: <path d="m9 6 6 6-6 6" />,
    "chevron-down": <path d="m6 9 6 6 6-6" />,
    plus: <path d="M12 5v14M5 12h14" />,
    copy: (
      <>
        <rect x="8" y="8" width="12" height="12" rx="2" />
        <path d="M15 8V4H4v11h4" />
      </>
    ),
    menu: <path d="M5 7h14M5 12h14M5 17h14" />,
    link: (
      <path d="M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1 1M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1-1" />
    ),
    eye: (
      <>
        <path d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12Z" />
        <circle cx="12" cy="12" r="3" />
      </>
    ),
    help: (
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="M9.5 9.5a2.5 2.5 0 1 1 3.5 2.3c-.7.3-1 .8-1 1.6v.4M12 17h.01" />
      </>
    ),
    back: <path d="M19 12H5m6-6-6 6 6 6" />,
    crown: (
      <path d="M4 18h16M5 15 3.5 7l5 3.5L12 5l3.5 5.5 5-3.5L19 15Z" />
    ),
    user: (
      <>
        <circle cx="12" cy="8" r="4" />
        <path d="M4.5 20a7.5 7.5 0 0 1 15 0" />
      </>
    ),
    users: (
      <>
        <circle cx="9" cy="8" r="3.5" />
        <path d="M2.5 19.5a6.5 6.5 0 0 1 13 0M16 4.8a3.5 3.5 0 0 1 0 6.4M18 13.5a6.5 6.5 0 0 1 3.5 6" />
      </>
    ),
    clock: (
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v5l3 2" />
      </>
    ),
    pencil: <path d="M4 20h4L19 9l-4-4L4 16v4ZM14 6l4 4" />,
    sliders: (
      <path d="M4 7h10m4 0h2M4 17h2m4 0h10M14 4v6M6 14v6" />
    ),
    swap: <path d="M7 4 3 8l4 4M3 8h14M17 20l4-4-4-4M21 16H7" />,
    lock: (
      <>
        <rect x="4.5" y="10.5" width="15" height="10" rx="2.5" />
        <path d="M8 10.5V7.5a4 4 0 0 1 8 0v3" />
      </>
    ),
    monitor: (
      <>
        <rect x="2.5" y="4" width="19" height="13" rx="2.5" />
        <path d="M8 21h8M12 17v4" />
      </>
    ),
    pause: <path d="M9 5v14M15 5v14" />,
    play: <path d="M7 4.5v15l12-7.5Z" />,
    door: (
      <path d="M14 4H6v16h8M10 12h11m-3-3 3 3-3 3" />
    ),
    share: (
      <path d="M12 3v12M7.5 7.5 12 3l4.5 4.5M5 12v7a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-7" />
    ),
    flag: <path d="M5 21V4h11l-2 4 2 4H5" />,
    maximize: (
      <path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5" />
    ),
    trophy: (
      <path d="M8 4h8v5a4 4 0 0 1-8 0V4ZM8 6H4.5a3.5 3.5 0 0 0 3.8 3.5M16 6h3.5a3.5 3.5 0 0 1-3.8 3.5M12 13v4m-4 3h8l-1-3H9l-1 3Z" />
    ),
  };
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {paths[name] ?? paths.help}
    </svg>
  );
}

/** Native modal <dialog>. Phones get a bottom sheet, larger screens a centred card.
 * Opening dispatches `after-hours:cover` so private surfaces close first. */
export function Dialog({
  title,
  children,
  onClose,
  wide = false,
  coverSecrets = true,
  className,
  game,
  banner,
  label,
}: {
  title: string;
  children: ReactNode;
  onClose: () => void;
  wide?: boolean;
  coverSecrets?: boolean;
  className?: string;
  /** Optional game id: the sheet takes that game's palette. */
  game?: string;
  /** Optional full-bleed art above the title (the close button floats over it). */
  banner?: ReactNode;
  /** Accessible name when it should say more than the visible title. */
  label?: string;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  useLayoutEffect(() => {
    const d = ref.current!;
    if (coverSecrets) window.dispatchEvent(new Event("after-hours:cover"));
    d.showModal();
    // showModal focuses the first control, which is usually the close button.
    // Safari then paints a focus ring on it for every tap-opened sheet; start on
    // the sheet itself instead (Tab still reaches the close button first).
    if (document.activeElement?.closest(".dialog-head"))
      d.focus({ preventScroll: true });
    const close = () => onClose();
    d.addEventListener("cancel", close);
    return () => {
      d.removeEventListener("cancel", close);
      d.close();
    };
  }, []);
  return (
    <dialog
      aria-label={label ?? title}
      className={`dialog${wide ? " wide" : ""}${banner ? " has-banner" : ""}${className ? ` ${className}` : ""}`}
      ref={ref}
      tabIndex={-1}
      data-game={game}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <span className="dialog-handle" aria-hidden="true" />
      {banner ? <div className="dialog-banner">{banner}</div> : null}
      <div className="dialog-head">
        <h2>{title}</h2>
        <button className="icon-button" aria-label="Close" onClick={onClose}>
          <Icon name="close" />
        </button>
      </div>
      {children}
    </dialog>
  );
}

const themeColors = { light: "#f6efe3", dark: "#15130f" };
/** Point the browser chrome (iOS/Android status and toolbar tint) at the page
 * background, which a game room tints with its palette. Falls back to the paper
 * colour when the computed colour cannot be read as sRGB (e.g. jsdom). */
export function syncThemeColor() {
  const meta = document.querySelector('meta[name="theme-color"]');
  if (!meta) return;
  const theme = document.documentElement.dataset.theme === "dark" ? "dark" : "light";
  let color: string = themeColors[theme];
  try {
    const bg = getComputedStyle(document.body).backgroundColor;
    const canvas = document.createElement("canvas");
    canvas.width = canvas.height = 1;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (ctx && bg && bg !== "transparent" && !bg.startsWith("rgba(0, 0, 0, 0")) {
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, 1, 1);
      const [r, g, b, a] = ctx.getImageData(0, 0, 1, 1).data;
      if (a === 255)
        color = `#${[r, g, b].map((v) => v.toString(16).padStart(2, "0")).join("")}`;
    }
  } catch {
    /* Keep the paper colour. */
  }
  meta.setAttribute("content", color);
}
export function ThemeControl() {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState(() => {
    try {
      const value = localStorage.getItem("after-hours-theme");
      return value === "light" || value === "dark" ? value : "system";
    } catch {
      return "system";
    }
  });
  useEffect(() => {
    const media = matchMedia("(prefers-color-scheme: dark)");
    const apply = () => {
      const theme =
        mode === "system" ? (media.matches ? "dark" : "light") : mode;
      document.documentElement.dataset.theme = theme;
      document.documentElement.style.colorScheme = theme;
      syncThemeColor();
    };
    apply();
    media.addEventListener("change", apply);
    return () => media.removeEventListener("change", apply);
  }, [mode]);
  useEffect(() => {
    if (!open) return;
    const key = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    addEventListener("keydown", key);
    return () => removeEventListener("keydown", key);
  }, [open]);
  const options = [
    ["system", "System", "Match this device", "system"],
    ["light", "Light", "Paper and ink", "sun"],
    ["dark", "Dark", "Lights down", "moon"],
  ] as const;
  return (
    <div className="theme-control">
      <button
        className="icon-button"
        aria-label={`Theme: ${mode}. Change theme`}
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        <Icon
          name={mode === "dark" ? "moon" : mode === "light" ? "sun" : "system"}
        />
      </button>
      {open ? (
        <>
          <button
            className="menu-dismiss"
            aria-label="Close theme menu"
            tabIndex={-1}
            onClick={() => setOpen(false)}
          />
          <div className="theme-menu" role="group" aria-label="Appearance">
            <p className="theme-menu-title" aria-hidden="true">
              Appearance
            </p>
            {options.map(([m, label, hint, icon]) => (
              <button
                key={m}
                aria-pressed={mode === m}
                aria-label={label}
                onClick={() => {
                  try {
                    localStorage.setItem("after-hours-theme", m);
                  } catch {
                    /* Theme still works for this visit. */
                  }
                  setMode(m);
                  setOpen(false);
                }}
              >
                <span className="theme-icon">
                  <Icon name={icon} size={18} />
                </span>
                <span className="theme-text">
                  <strong>{label}</strong>
                  <small>{hint}</small>
                </span>
                {mode === m ? <Icon name="check" size={18} /> : null}
              </button>
            ))}
          </div>
        </>
      ) : null}
    </div>
  );
}

export function Header({
  onHome,
  children,
  compact = false,
  minimal = false,
}: {
  onHome: () => void;
  children?: ReactNode;
  compact?: boolean;
  /** Public display during play: no theme control, only what the children add. */
  minimal?: boolean;
}) {
  return (
    <header
      className={`site-header${compact ? " is-compact" : ""}${minimal ? " is-minimal" : ""}`}
    >
      <button
        className="wordmark"
        onClick={onHome}
        aria-label="After Hours home"
      >
        after hours<span className="wordmark-dot">✳</span>
      </button>
      <nav aria-label="Site">
        {children}
        {minimal ? null : <ThemeControl />}
      </nav>
    </header>
  );
}

export function Rules({
  game,
  onClose,
  onPractice,
}: {
  game: GameInfo;
  onClose: () => void;
  /** Opened from the catalogue: start the practice round in this tab. Without it
   * (in a room) the practice round opens in a new tab so the room is untouched. */
  onPractice?: () => void;
}) {
  // The footer's single action is the practice round; inside one it's omitted
  // (the sheet closes with ✕, Esc or the backdrop).
  const inPractice =
    typeof location !== "undefined" &&
    typeof location.pathname === "string" &&
    location.pathname.startsWith("/learn/");
  const offerPractice = !inPractice && hasPractice(game.id);
  return (
    <Dialog
      title={`How to play ${game.name}`}
      onClose={onClose}
      className="rules-dialog"
      game={game.id}
    >
      <div className="rules-body">
        <div
          className={`rules-art art ${game.id}`}
          role="img"
          aria-label={`${game.name} illustration`}
        />
        <div className="chip-row">
          <span className="chip">
            <Icon name="users" size={16} />
            {game.players} players
          </span>
          <span className="chip">
            <Icon name="clock" size={16} />
            {game.minutes} min
          </span>
        </div>
        <ol className="rules-steps">
          {game.steps.map((s) => (
            <li key={s}>{s}</li>
          ))}
        </ol>
        <details className="rules-more">
          <summary>
            <span>More rules</span>
            <Icon name="chevron-down" />
          </summary>
          <p>{game.detail}</p>
          <p>
            Play together in person, or keep a voice/video call open for
            remote play. Each player needs their own screen for private
            information. The host can pause without changing anyone’s choices.
          </p>
        </details>
        {game.id === "secret-hitler" ? (
          <p className="credits">
            Noncommercial adaptation of Secret Hitler by Mike Boxleiter, Tommy
            Maranges and Mac Schubert.{" "}
            <a
              href="https://creativecommons.org/licenses/by-nc-sa/4.0/"
              target="_blank"
              rel="noreferrer"
            >
              CC BY-NC-SA 4.0
            </a>
            . Original web art, interface, accessibility and automated rules.
            Not affiliated with the creators.{" "}
            <a
              href="https://www.secrethitler.com/assets/Secret_Hitler_Rules.pdf"
              target="_blank"
              rel="noreferrer"
            >
              Official rules
            </a>
            .
          </p>
        ) : null}
        {offerPractice ? (
          <div className="sheet-footer">
            {onPractice ? (
              <button className="primary full" onClick={onPractice}>
                <Icon name="play" /> Try a practice round
              </button>
            ) : (
              <a className="primary full" href={practicePath(game.id)} target="_blank" rel="noopener">
                <Icon name="play" /> Try a practice round
                <span className="sr-only"> (opens in a new tab)</span>
              </a>
            )}
          </div>
        ) : null}
      </div>
    </Dialog>
  );
}

function initials(name: string) {
  return [...name.trim().split(/\s+/).slice(0, 2)]
    .map((w) => [...w][0] ?? "")
    .join("")
    .toUpperCase();
}

/** Seat-coloured initials. Colour comes from seat index only, never role or team. */
export function Avatar({
  name,
  index = 0,
  ready = false,
  small = false,
  host = false,
  size,
}: {
  name: string;
  index?: number;
  ready?: boolean;
  small?: boolean;
  host?: boolean;
  size?: 28 | 40 | 56;
}) {
  const px = size ?? (small ? 32 : 56);
  const seat = ((index % 8) + 8) % 8;
  return (
    <span
      className={`avatar seat-${seat}${small ? " small" : ""} size-${px}`}
      aria-hidden="true"
    >
      {initials(name)}
      {host ? (
        <span className="avatar-badge host-badge">
          <Icon name="crown" size={12} />
        </span>
      ) : null}
      {ready ? (
        <span className="ready-tick">
          <Icon name="check" size={12} />
        </span>
      ) : null}
    </span>
  );
}

/** Choose a game as a grid of mini covers (native radios underneath). */
export function GamePicker({
  value,
  onChange,
  label = "Game",
}: {
  value: string;
  onChange: (id: string) => void;
  label?: string;
}) {
  return (
    <fieldset className="ui-field game-picker">
      <legend className="ui-label">{label}</legend>
      <div className="game-picker-grid">
        {games.map((g) => (
          <label
            key={g.id}
            className={`game-pick${value === g.id ? " is-checked" : ""}`}
            data-game={g.id}
          >
            <input
              type="radio"
              className="ui-native"
              name="game-picker"
              value={g.id}
              checked={value === g.id}
              onChange={() => onChange(g.id)}
            />
            <span className={`art ${g.id}`} aria-hidden="true" />
            <span className="game-pick-text">
              <strong>{g.name}</strong>
              <small>{g.players} players</small>
            </span>
            <span className="choice-check" aria-hidden="true">
              <Icon name="check" size={14} />
            </span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

/** Pick a game in one tap: each cover is a button that acts immediately (used by
 * "Change game", where choosing saves and returns to the lobby). */
export function GameChooser({
  value,
  onChoose,
  disabled = false,
}: {
  value: string;
  onChoose: (id: string) => void;
  disabled?: boolean;
}) {
  return (
    <div className="ui-field game-picker">
      <div className="game-picker-grid">
        {games.map((g) => (
          <button
            type="button"
            key={g.id}
            className={`game-pick${value === g.id ? " is-checked" : ""}`}
            data-game={g.id}
            disabled={disabled}
            aria-current={value === g.id ? "true" : undefined}
            aria-label={
              value === g.id
                ? `${g.name}, current game. Keep ${g.name}`
                : `Switch to ${g.name}`
            }
            onClick={() => onChoose(g.id)}
          >
            <span className={`art ${g.id}`} aria-hidden="true" />
            <span className="game-pick-text">
              <strong>{g.name}</strong>
              <small>{g.players} players</small>
            </span>
            <span className="choice-check" aria-hidden="true">
              <Icon name="check" size={14} />
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}
